import React from "react";
import { Link } from "react-router-dom";
import { Crown, Music, Megaphone, TrendingUp, Briefcase, Headphones, Sparkles } from "lucide-react";

/**
 * Footer — three-column site footer that appears on every page above the
 * BottomPlayer. Brutalist-editorial style: heavy uppercase headings, low-
 * key link styling, vermilion accents on hover.
 *
 * Structure
 * ─────────
 *   COMPANY              · About, Jobs for Producers & Engineers      → /about · /jobs
 *   COMMUNITIES          · Artists, Advertising, Become an Investor   → /upload · /promote · /investors
 *   1O1SOUND PLANS       · Premium / Listener / Artist                → /premium · /plans
 *
 * Each column header is now itself a navigable Link (→ hub page), and
 * every sub-item routes to a real in-app page (no mailto fallbacks).
 */
const FooterLink = ({ to, icon: Icon, label, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group inline-flex items-center gap-2 text-sm text-white/55 hover:text-white transition py-1.5"
  >
    {Icon && <Icon size={13} className="opacity-70 group-hover:text-vermilion transition" strokeWidth={2.2} />}
    <span>{label}</span>
  </Link>
);

const ColumnHeader = ({ kicker, title, to, testId }) => (
  <Link to={to} data-testid={testId} className="block mb-4 group">
    <p className="text-[10px] uppercase tracking-[0.28em] text-vermilion font-black mb-1.5">
      {kicker}
    </p>
    <h3 className="font-display text-xl font-black uppercase tracking-tight leading-none group-hover:text-vermilion transition">
      {title}
    </h3>
  </Link>
);

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="mt-20 border-t border-white/8 bg-gradient-to-b from-transparent to-black/60 -mx-4 sm:-mx-8 px-4 sm:px-8 pt-12 pb-10"
    >
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12">
        {/* Company */}
        <div>
          <ColumnHeader kicker="01" title="Company" to="/about" testId="footer-col-company" />
          <nav className="flex flex-col">
            <FooterLink to="/about" icon={Sparkles} label="About" testId="footer-about" />
            <FooterLink to="/jobs" icon={Briefcase} label="Jobs for Producers & Engineers" testId="footer-jobs" />
          </nav>
        </div>

        {/* Communities */}
        <div>
          <ColumnHeader kicker="02" title="Communities" to="/communities" testId="footer-col-communities" />
          <nav className="flex flex-col">
            <FooterLink to="/upload" icon={Music} label="Artists" testId="footer-artists" />
            <FooterLink to="/promote" icon={Megaphone} label="Advertising" testId="footer-advertising" />
            <FooterLink to="/investors" icon={TrendingUp} label="Become an Investor" testId="footer-investor" />
          </nav>
        </div>

        {/* 1o1sound Plans */}
        <div>
          <ColumnHeader kicker="03" title="1o1sound Plans" to="/plans" testId="footer-col-plans" />
          <nav className="flex flex-col">
            <FooterLink to="/premium" icon={Crown} label="Premium" testId="footer-premium" />
            <FooterLink to="/premium" icon={Headphones} label="Listener Premium · $10/mo" testId="footer-premium-listener" />
            <FooterLink to="/premium" icon={Music} label="Artist Premium · $25/mo · 3 mo free" testId="footer-premium-artist" />
          </nav>
        </div>
      </div>

      {/* Bottom bar — logo + legal */}
      <div className="max-w-[1600px] mx-auto mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <Link to="/" data-testid="footer-logo" className="flex items-center gap-2">
          <span className="font-display text-2xl font-black uppercase tracking-tight">
            <span className="text-white">1o1</span>
            <span className="text-vermilion">sound</span>
          </span>
        </Link>
        <p className="text-[11px] text-white/35 uppercase tracking-[0.18em]">
          © 2026 1o1sound · Built for artists, by artists
        </p>
      </div>
    </footer>
  );
};

export default Footer;
